import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { WarningRounded } from "@material-ui/icons";
import {
  confirmClientRegistration,
  generatePasswordSetupLink as requestPasswordSetupLink,
  getClients,
} from "../../api";
import ModalConfirm from "../../components/ModalConfirm";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { jdsTableStyles } from "../../components/jds/tableStyles";
import { JdsPagination } from "../../components/jds/JdsPagination";
import { JdsButton } from "../../components/jds/JdsButton";
import { JdsSearchInput } from "../../components/jds/JdsSearchInput";
import { JdsTag } from "../../components/jds/JdsTag";
import { JdsChip } from "../../components/jds/JdsChip";
import { EyeIcon, PlusIcon } from "../../components/jds/icons";
import "../../components/jds/jds.css";

const FILTER_DEBOUNCE_MS = 400;

const rowActionsStyle = {
  display: "flex",
  alignItems: "center",
  gap: "var(--space-8)",
};
const subHeaderStyle = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "var(--space-8)",
  width: "100%",
};
const filtersStyle = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "var(--space-8)",
};

function getVerificationState(row) {
  if (row.verified && !row.first_access_required) {
    return { status: "Ativo", kind: "active", color: "success" };
  }
  if (row.verified && row.first_access_required) {
    return {
      status: "E-mail ok, senha pendente",
      kind: "password_pending",
      color: "info",
    };
  }
  return { status: "Pendente", kind: "pending", color: "warning" };
}

function getVerificationStatus(row) {
  return getVerificationState(row).status;
}

// `provider` pode estar ausente até o backend ser publicado;
// valor ausente/desconhecido não renderiza nada.
const BILLING_PROVIDERS = {
  jussub: { label: "JusSub", color: "brand" },
  jusbill: { label: "JusBill", color: "neutral" },
};

function getBillingProvider(row) {
  const key =
    typeof row.provider === "string"
      ? row.provider.trim().toLowerCase()
      : "";
  return Object.prototype.hasOwnProperty.call(BILLING_PROVIDERS, key)
    ? BILLING_PROVIDERS[key]
    : null;
}

export function Clientes() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [actionClientId, setActionClientId] = useState(null);
  const [clientToConfirm, setClientToConfirm] = useState(null);
  const [confirmError, setConfirmError] = useState("");
  const [passwordSetupLink, setPasswordSetupLink] = useState(null);

  const [filters, setFilters] = useState({
    name: "",
    email: "",
    document: "",
  });

  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
  });

  const debouncedName = useDebouncedValue(filters.name, FILTER_DEBOUNCE_MS);
  const debouncedEmail = useDebouncedValue(filters.email, FILTER_DEBOUNCE_MS);
  const debouncedDocument = useDebouncedValue(filters.document, FILTER_DEBOUNCE_MS);

  const requestIdRef = useRef(0);

  const fetchClients = async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const response = await getClients({
        page: pagination.page,
        perPage: pagination.perPage,
        name: debouncedName,
        email: debouncedEmail,
        document: debouncedDocument,
      });
      if (requestId !== requestIdRef.current) return;
      setData(response.data.data);
      setTotalRows(response.data.total);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  const copyPasswordSetupLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado!");
    } catch (_error) {
      toast.error("Não foi possível copiar o link.");
    }
  };

  const openPasswordSetupLinkModal = (result) => {
    if (!result?.create_password_url || !result?.email) {
      return false;
    }

    setPasswordSetupLink({
      url: result.create_password_url,
      email: result.email,
      expiresIn: result.expires_in,
    });
    return true;
  };

  const closeConfirmModal = () => {
    if (actionClientId != null) {
      return;
    }
    setClientToConfirm(null);
    setConfirmError("");
  };

  const confirmRegistration = async () => {
    if (!clientToConfirm?.id || actionClientId != null) {
      return;
    }

    const clientId = clientToConfirm.id;
    setConfirmError("");
    setActionClientId(clientId);
    try {
      const { data: result } = await confirmClientRegistration(clientId);
      toast.success("Cadastro confirmado com sucesso!");
      setClientToConfirm(null);
      setConfirmError("");

      if (result?.first_access_required) {
        const opened = openPasswordSetupLinkModal(result);
        if (!opened) {
          toast.error(
            "Cadastro confirmado, mas o link de criação de senha não veio completo."
          );
        }
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Não foi possível confirmar o cadastro.";
      setConfirmError(message);
      toast.error(message);
      return;
    } finally {
      setActionClientId((current) => (current === clientId ? null : current));
    }

    try {
      await fetchClients();
    } catch (_error) {
      toast.error("Cadastro confirmado, mas não foi possível atualizar a lista.");
    }
  };

  const handleGeneratePasswordSetupLink = async (clientId) => {
    if (actionClientId != null) {
      return;
    }

    setConfirmError("");
    setActionClientId(clientId);
    try {
      const { data: result } = await requestPasswordSetupLink(clientId);
      const opened = openPasswordSetupLinkModal(result);
      if (!opened) {
        toast.error(
          "A resposta do link de criação de senha veio incompleta."
        );
        return;
      }
      toast.success("Link de criação de senha gerado.");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Não foi possível gerar o link de criação de senha.";
      toast.error(message);
    } finally {
      setActionClientId((current) => (current === clientId ? null : current));
    }
  };

  const whatsapp = (fullName, phone, email) => {
    const firstName = fullName.split(" ")[0];
    const text = `Olá ${firstName}, tudo bem? Somos da plataforma jurídica Jusfy e o seu e-mail ${email} ainda não foi confirmado, este e-mail está correto? Podemos confirmar para liberar o seu acesso?`;
    const phoneOnlyNumbers = phone
      .replace("(", "")
      .replace(")", "")
      .replace("-", "")
      .replace(" ", "");
    const phoneInternational = `55${phoneOnlyNumbers}`;

    window.open(
      `https://api.whatsapp.com/send?phone=${phoneInternational}&text=${text}`,
      "_blank"
    );
  };

  const columns = [
    {
      name: "Ação",
      cell: (row) => (
        <Link target="_BLANK" to={`/clientes/${row.id}`}>
          <JdsButton
            variant="ghost"
            color={!row.deleted_at ? "neutral" : "error"}
            size="sm"
            iconOnly
            aria-label={
              !row.deleted_at ? "Ver cliente" : "Ver cliente excluído"
            }
            title={!row.deleted_at ? "Ver cliente" : "Ver cliente excluído"}
          >
            <EyeIcon />
          </JdsButton>
        </Link>
      ),
      width: "80px"
    },
    {
      name: "Cadastrado em",
      selector: row => row.created_at,
      width: "150px"
    },
    {
      name: "Nome",
      selector: row => row.name,
      width: "150px"
    },
    {
      name: "E-mail",
      selector: row => row.email,
      cell: (row) => {
        const provider = getBillingProvider(row);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-8)", minWidth: 0, width: "100%" }}>
            <span
              title={row.email}
              style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
            >
              {row.email}
            </span>
            {provider && (
              <span title={`Cliente ${provider.label}`} style={{ flexShrink: 0 }}>
                <JdsChip label={provider.label} color={provider.color} />
              </span>
            )}
          </div>
        );
      },
      width: "230px",
    },
    {
      name: "Telefone",
      selector: row => row.phone,
      width: "150px"
    },
    {
      name: "CPF",
      selector: row => row.document_number,
      width: "150px"
    },
    {
      name: "Cidade",
      selector: row => `${row.city_name} - ${row.state}`
    },
    {
      name: "Profissão",
      selector: row => row.profession_name
    },
    {
      name: "Status",
      selector: row => getVerificationStatus(row),
      cell: (row) => {
        const { status, color } = getVerificationState(row);
        return <JdsTag label={status} color={color} dot />;
      },
      width: "240px",
    },
    {
      name: "Verificado",
      cell: (row) => {
        const { kind } = getVerificationState(row);

        if (kind === "active") {
          return "Sim";
        }

        if (kind === "password_pending") {
          return (
            <div style={rowActionsStyle}>
              <JdsButton
                size="sm"
                disabled={actionClientId != null}
                onClick={() => handleGeneratePasswordSetupLink(row.id)}
              >
                {actionClientId === row.id
                  ? "Gerando..."
                  : "Gerar link de senha"}
              </JdsButton>
              <JdsButton
                variant="outlined"
                color="neutral"
                size="sm"
                onClick={() => whatsapp(row.name, row.phone, row.email)}
              >
                WhatsApp
              </JdsButton>
            </div>
          );
        }

        return (
          <div style={rowActionsStyle}>
            <JdsButton
              size="sm"
              disabled={actionClientId != null}
              onClick={() => {
                setConfirmError("");
                setClientToConfirm(row);
              }}
            >
              Confirmar cadastro
            </JdsButton>
            <JdsButton
              variant="outlined"
              color="neutral"
              size="sm"
              onClick={() => whatsapp(row.name, row.phone, row.email)}
            >
              WhatsApp
            </JdsButton>
          </div>
        );
      },
      width: "360px",
    }
  ];

  useEffect(() => {
    fetchClients();
  }, [
    pagination.page,
    pagination.perPage,
    debouncedName,
    debouncedEmail,
    debouncedDocument,
  ]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handlePerPageChange = (newPerPage, page) => {
    setPagination({ page, perPage: newPerPage });
  };

  const subHeaderComponent = useMemo(() => (
    <div style={subHeaderStyle}>
      <div style={filtersStyle}>
        <JdsSearchInput
          id="clientes-filter-name"
          placeholder="Nome"
          filterText={filters.name}
          onFilter={(e) => handleFilterChange("name", e.target.value)}
          onClear={() => handleFilterChange("name", "")}
        />
        <JdsSearchInput
          id="clientes-filter-email"
          placeholder="E-mail"
          filterText={filters.email}
          onFilter={(e) => handleFilterChange("email", e.target.value)}
          onClear={() => handleFilterChange("email", "")}
        />
        <JdsSearchInput
          id="clientes-filter-document"
          placeholder="CPF/CNPJ"
          filterText={filters.document}
          onFilter={(e) =>
            handleFilterChange("document", e.target.value.replace(/\D/g, ""))
          }
          onClear={() => handleFilterChange("document", "")}
        />
      </div>
      <Link to="/clientes/add">
        <JdsButton>
          Novo cliente
          <PlusIcon />
        </JdsButton>
      </Link>
    </div>
  ), [filters]);

  const isActing = actionClientId != null;

  const passwordSetupMessage = passwordSetupLink
    ? [
        `Envie este link para ${passwordSetupLink.email}.`,
        passwordSetupLink.expiresIn
          ? `Válido por ${passwordSetupLink.expiresIn}.`
          : null,
        "Se expirar ou o cliente não criar a senha, gere novamente.",
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  return (
    <div className="jds-scope">
      <h1 className="jds-title-lg">Clientes</h1>
      <p className="text-muted mb-10">Listagem de todos os clientes</p>

      <div className="jds-card jds-card--flush">
        <div style={{ overflowX: "auto", width: "100%" }}>
          <DataTable
            columns={columns}
            data={data}
            customStyles={jdsTableStyles}
            progressPending={loading}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerPageChange}
            paginationComponent={JdsPagination}
            subHeader
            subHeaderAlign="left"
            subHeaderComponent={subHeaderComponent}
            responsive={false}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      {clientToConfirm && (
        <ModalConfirm
          icon={WarningRounded}
          title="Confirmar cadastro?"
          message={`O e-mail de ${clientToConfirm.name || "este cliente"} será marcado como verificado. Se estiver no primeiro acesso, geraremos um link de criação de senha para você copiar e enviar (ex.: WhatsApp).`}
          onConfirm={confirmRegistration}
          onClose={closeConfirmModal}
          isLoading={isActing}
        >
          {confirmError ? (
            <p className="text-danger" style={{ marginTop: "var(--space-8)" }}>
              {confirmError}
            </p>
          ) : null}
        </ModalConfirm>
      )}

      {passwordSetupLink && (
        <ModalConfirm
          icon={WarningRounded}
          title="Link de criação de senha"
          message={passwordSetupMessage}
          oneButtonMessage="Fechar"
          onConfirm={() => setPasswordSetupLink(null)}
          onClose={() => setPasswordSetupLink(null)}
        >
          <div style={{ textAlign: "left", marginTop: "var(--space-8)" }}>
            <input
              className="form-control"
              readOnly
              aria-label="Link de criação de senha"
              value={passwordSetupLink.url}
              style={{ fontSize: "var(--fs-13)", marginBottom: "var(--space-12)" }}
              onFocus={(event) => event.target.select()}
            />
            <JdsButton
              className="w-100"
              onClick={() => copyPasswordSetupLink(passwordSetupLink.url)}
            >
              Copiar link
            </JdsButton>
          </div>
        </ModalConfirm>
      )}
    </div>
  );
}
